/**
 * timesheetOffline.ts
 *
 * Offline-aware timesheet submission.
 *
 * - Online  → writes directly to Supabase, returns the created/updated record.
 * - Offline → enqueues in IndexedDB, returns a local optimistic record.
 *
 * Call syncPendingTimesheets() when the device comes back online.
 */

import { supabase } from '../lib/supabaseClient';
import { enqueue, getAll, dequeue, incrementRetries } from './offlineQueue';

export interface TimesheetPayload {
  assignment_id: string;
  vikar_user_id: string;
  clock_in_datetime: string;
  clock_out_datetime: string;
  break_minutes: number;
  total_hours: number;
  billable_hours: number;
  hourly_rate_applied: number;
  total_amount: number;
  vikar_notes?: string;
  status?: string;
}

export interface SubmitResult {
  /** true if written to Supabase, false if queued offline */
  synced: boolean;
  /** The record (remote or local-optimistic) */
  data: Record<string, unknown>;
}

// ─── Online helpers ───────────────────────────────────────────────────────────

async function createRemote(payload: TimesheetPayload): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('timesheets')
    .insert({ ...payload, created_at: new Date().toISOString() })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as Record<string, unknown>;
}

async function updateRemote(id: string, payload: Partial<TimesheetPayload>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('timesheets')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as Record<string, unknown>;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Submit a new timesheet. Routes to Supabase or offline queue based on connectivity.
 */
export async function submitTimesheet(payload: TimesheetPayload): Promise<SubmitResult> {
  if (navigator.onLine) {
    try {
      const data = await createRemote(payload);
      return { synced: true, data };
    } catch (err) {
      // Network call failed despite navigator.onLine — queue it
      console.warn('[timesheetOffline] Online submit failed, queuing:', err);
    }
  }

  const queued = await enqueue({ operation: 'create', payload: payload as unknown as Record<string, unknown> });
  return {
    synced: false,
    data: {
      ...payload,
      id: queued.localId,
      _offline: true,
      _localId: queued.localId,
    },
  };
}

/**
 * Submit an update to an existing timesheet. Routes to Supabase or offline queue.
 */
export async function submitTimesheetUpdate(
  remoteId: string,
  payload: Partial<TimesheetPayload>,
): Promise<SubmitResult> {
  if (navigator.onLine) {
    try {
      const data = await updateRemote(remoteId, payload);
      return { synced: true, data };
    } catch (err) {
      console.warn('[timesheetOffline] Online update failed, queuing:', err);
    }
  }

  const queued = await enqueue({
    operation: 'update',
    remoteId,
    payload: payload as Record<string, unknown>,
  });
  return {
    synced: false,
    data: {
      ...payload,
      id: remoteId,
      _offline: true,
      _localId: queued.localId,
    },
  };
}

export interface SyncReport {
  succeeded: number;
  failed: number;
  remaining: number;
}

/**
 * Flush the offline queue to Supabase.
 * Called automatically by useOfflineSync when network is restored.
 * Safe to call manually (e.g. on pull-to-refresh).
 */
export async function syncPendingTimesheets(): Promise<SyncReport> {
  const items = await getAll();
  let succeeded = 0;
  let failed = 0;

  for (const item of items) {
    try {
      if (item.operation === 'create') {
        await createRemote(item.payload as unknown as TimesheetPayload);
      } else if (item.operation === 'update' && item.remoteId) {
        await updateRemote(item.remoteId, item.payload as Partial<TimesheetPayload>);
      }
      await dequeue(item.localId);
      succeeded++;
    } catch (err) {
      console.warn(`[timesheetOffline] Sync failed for ${item.localId}:`, err);
      await incrementRetries(item.localId);
      failed++;
    }
  }

  const remaining = (await getAll()).length;
  return { succeeded, failed, remaining };
}
